import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    Form,
    ListGroup,
    Modal,
    Offcanvas,
    Spinner,
    Table,
    Tabs,
    Tab,
    Badge,
} from "react-bootstrap";
import { Gear, Trash } from "react-bootstrap-icons";
import SalariesEmployeeCard from "../components/SalariesEmployeeCard.jsx";
import { AppCheckbox, AppMonthField } from "../components/form";
import { authFetch } from "../api/authFetch";
import { roleLabel } from "../helpers/roles";
import { useNavigate, useParams } from "react-router-dom";
import "./SalariesPage.css";

/** Вкладка сторінки → шлях у URL /salaries/<slug>; /salaries без вкладки веде на «Розрахунок». */
const SALARIES_TAB_SLUGS = {
    desk: "rozrakhunok",
    "crm-dict": "dovidnyk-crm",
    presets: "nabory-filtriv",
    advance: "lyshe-avans",
    profiles: "profili-zp",
};
const SALARIES_SLUG_TO_TAB = Object.fromEntries(
    Object.entries(SALARIES_TAB_SLUGS).map(([key, slug]) => [slug, key])
);

function ymNow() {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    return `${y}-${m}`;
}

function formatMoney(n) {
    return `${Number(n) || 0} грн`;
}

function employeeDisplayName(emp) {
    if (emp?.linkedName) return emp.linkedName;
    return emp?.displayName || "";
}

/** Відображення часу останньої відправки розрахунку в Telegram. */
function formatTgSentAt(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString("uk-UA", { dateStyle: "short", timeStyle: "short" });
    } catch {
        return String(iso);
    }
}

function numsToCsv(arr) {
    if (!Array.isArray(arr) || !arr.length) return "";
    return arr.join(", ");
}

function normalizeHexForBadge(hex) {
    if (!hex || typeof hex !== "string") return "";
    let s = hex.trim();
    if (!s) return "";
    if (!s.startsWith("#")) s = `#${s}`;
    if (/^#[0-9A-Fa-f]{6}$/i.test(s)) return s.toUpperCase();
    if (/^#[0-9A-Fa-f]{3}$/i.test(s)) {
        const r = s[1];
        const g = s[2];
        const b = s[3];
        return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
    }
    return "";
}

/** Для сортування довідника: відтінок HSL; без валідного hex — null. */
function hexToHslHueSatLight(hex) {
    const norm = normalizeHexForBadge(hex);
    if (!norm) return null;
    const h = norm.replace("#", "");
    const r = parseInt(h.slice(0, 2), 16) / 255;
    const g = parseInt(h.slice(2, 4), 16) / 255;
    const b = parseInt(h.slice(4, 6), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    let hue = 0;
    let sat = 0;
    if (max !== min) {
        const d = max - min;
        sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                hue = ((g - b) / d + (g < b ? 6 : 0)) / 6;
                break;
            case g:
                hue = ((b - r) / d + 2) / 6;
                break;
            default:
                hue = ((r - g) / d + 4) / 6;
                break;
        }
    }
    return { h: hue * 360, s: sat, l };
}

/** Статуси/теги: за кольором (веселка по hue), сірі без насиченості разом; без кольору — в кінці; далі за назвою. */
function sortCrmRefRowsByColor(rows) {
    return [...rows].sort((a, b) => {
        const ca = hexToHslHueSatLight(a.colorHex);
        const cb = hexToHslHueSatLight(b.colorHex);
        const titleCmp = String(a.title || "").localeCompare(String(b.title || ""), "uk");
        if (!ca && !cb) return titleCmp;
        if (!ca) return 1;
        if (!cb) return -1;
        if (ca.h !== cb.h) return ca.h - cb.h;
        if (ca.s !== cb.s) return cb.s - ca.s;
        if (ca.l !== cb.l) return ca.l - cb.l;
        return titleCmp;
    });
}

function textColorOnBackground(hex6) {
    const h = (hex6 || "").replace("#", "");
    if (h.length !== 6) return "#212529";
    const r = parseInt(h.slice(0, 2), 16) / 255;
    const g = parseInt(h.slice(2, 4), 16) / 255;
    const b = parseInt(h.slice(4, 6), 16) / 255;
    const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return L > 0.55 ? "#1a1a1a" : "#ffffff";
}

/** Бейдж як у KeyCRM: фон з hex, контрастний текст. */
function CrmColoredBadge({ label, colorHex }) {
    const bg = normalizeHexForBadge(colorHex) || "#6c757d";
    const fg = textColorOnBackground(bg);
    return (
        <span
            className="rounded px-2 py-1 small d-inline-block"
            style={{
                backgroundColor: bg,
                color: fg,
                border: "1px solid rgba(0,0,0,.18)",
                maxWidth: "100%",
                whiteSpace: "normal",
                wordBreak: "break-word",
            }}
        >
            {label}
        </span>
    );
}

const SalariesPage = ({ user }) => {
    const [yearMonth, setYearMonth] = useState(ymNow());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [rows, setRows] = useState([]);
    /** Фактична фіксація на сервері — поки true, рядки лише для перегляду. */
    const [serverFinalized, setServerFinalized] = useState(false);
    const [monthFinalizedAt, setMonthFinalizedAt] = useState(null);
    const [tgSendModal, setTgSendModal] = useState(null);
    const [tgRevokingId, setTgRevokingId] = useState(null);
    const [tgSuccess, setTgSuccess] = useState("");
    const [paidSaving, setPaidSaving] = useState(false);

    const applyMonthResponse = useCallback((data) => {
        if (!data) return;
        setRows(data.rows || []);
        const fin = data.finalized === true;
        setServerFinalized(fin);
        setMonthFinalizedAt(data.finalizedAt || null);
    }, []);

    const derivedTotals = useMemo(() => {
        const advanceTotal = Math.round(
            rows.reduce((s, r) => s + (r.employee.hasAdvance !== false ? r.computed.advance : 0), 0)
        );
        const salaryDayTotal = Math.round(rows.reduce((s, r) => s + r.computed.payOnSalaryDay, 0));
        const salaryDayRemaining = Math.round(
            rows.reduce((s, r) => s + (r.line?.paid === true ? 0 : r.computed.payOnSalaryDay), 0)
        );
        const accruedTotal = Math.round(rows.reduce((s, r) => s + r.computed.accrued, 0));
        return { advanceTotal, salaryDayTotal, salaryDayRemaining, accruedTotal };
    }, [rows]);

    const paidProgress = useMemo(() => {
        const total = rows.length;
        const done = rows.filter((r) => r.line?.paid === true).length;
        return { done, total };
    }, [rows]);

    const tabParam = (useParams()["*"] || "").split("/")[0];
    const navigate = useNavigate();
    const tab = SALARIES_SLUG_TO_TAB[tabParam] || "desk";
    const setTab = useCallback(
        (key) => navigate(`/salaries/${SALARIES_TAB_SLUGS[key] || SALARIES_TAB_SLUGS.desk}`),
        [navigate]
    );

    useEffect(() => {
        if (!SALARIES_SLUG_TO_TAB[tabParam]) {
            navigate(`/salaries/${SALARIES_TAB_SLUGS.desk}${window.location.search}`, { replace: true });
        }
    }, [tabParam, navigate]);

    const [allProfiles, setAllProfiles] = useState([]);
    const [users, setUsers] = useState([]);
    const [showAllProfiles, setShowAllProfiles] = useState(false);
    const [filterPresets, setFilterPresets] = useState([]);
    const [crmStatuses, setCrmStatuses] = useState([]);
    const [crmTags, setCrmTags] = useState([]);
    const crmStatusesSorted = useMemo(() => sortCrmRefRowsByColor(crmStatuses), [crmStatuses]);
    const crmTagsSorted = useMemo(() => sortCrmRefRowsByColor(crmTags), [crmTags]);
    const [crmLoading, setCrmLoading] = useState(false);
    const [crmInfo, setCrmInfo] = useState(null);
    const [crmError, setCrmError] = useState("");
    const [crmWarnings, setCrmWarnings] = useState([]);

    const [scheduleLoading, setScheduleLoading] = useState(false);
    const [scheduleInfo, setScheduleInfo] = useState(null);
    const [scheduleError, setScheduleError] = useState("");
    const [scheduleWarnings, setScheduleWarnings] = useState([]);

    const [editOpen, setEditOpen] = useState(false);
    const [editForm, setEditForm] = useState(null);

    const [refModalOpen, setRefModalOpen] = useState(false);
    const [refKind, setRefKind] = useState("order_status");
    const [refForm, setRefForm] = useState({ title: "", crmId: "", colorHex: "" });

    const [presetModalOpen, setPresetModalOpen] = useState(false);
    const [presetForm, setPresetForm] = useState(null);
    const [salariesToolsOpen, setSalariesToolsOpen] = useState(false);

    const loadFilterPresets = useCallback(async () => {
        try {
            const res = await authFetch("/api/salary/filter-presets", { credentials: "include" });
            if (res.ok) setFilterPresets(await res.json());
        } catch {
            /* ignore */
        }
    }, []);

    const loadCrmReference = useCallback(async () => {
        try {
            const [st, tg] = await Promise.all([
                authFetch("/api/salary/crm-reference?kind=order_status", { credentials: "include" }).then((r) =>
                    r.ok ? r.json() : []
                ),
                authFetch("/api/salary/crm-reference?kind=order_tag", { credentials: "include" }).then((r) =>
                    r.ok ? r.json() : []
                ),
            ]);
            setCrmStatuses(Array.isArray(st) ? st : []);
            setCrmTags(Array.isArray(tg) ? tg : []);
        } catch {
            /* ignore */
        }
    }, []);

    useEffect(() => {
        loadFilterPresets();
    }, [loadFilterPresets]);

    useEffect(() => {
        if (tab === "crm-dict" || tab === "presets") loadCrmReference();
    }, [tab, loadCrmReference]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError("");
            let data = null;
            try {
                const res = await authFetch(`/api/salary/month/${yearMonth}`, { credentials: "include" });
                if (!res.ok) throw new Error(await res.text());
                data = await res.json();
                if (!cancelled) applyMonthResponse(data);
            } catch (e) {
                if (!cancelled) setError(e.message || "Помилка завантаження");
            } finally {
                if (!cancelled) setLoading(false);
            }

            if (cancelled || !data) return;

            const skipCrm = data.finalized === true;
            if (skipCrm) {
                setCrmInfo(null);
                setCrmWarnings([]);
                setCrmError("");
                setCrmLoading(false);
                setScheduleInfo(null);
                setScheduleWarnings([]);
                setScheduleError("");
                setScheduleLoading(false);
                return;
            }

            setCrmLoading(true);
            setCrmError("");
            try {
                const crmRes = await authFetch(
                    `/api/salary/crm-sync?yearMonth=${encodeURIComponent(yearMonth)}`,
                    { credentials: "include" }
                );
                if (cancelled) return;
                if (crmRes.ok) {
                    const crmData = await crmRes.json();
                    setCrmInfo({ ordersCount: crmData.ordersCount });
                    setCrmWarnings(crmData.warnings || []);
                    const turnovers = crmData.turnovers || {};
                    setRows((prev) =>
                        prev.map((r) => {
                            const t = turnovers[String(r.employee._id)];
                            if (t === undefined) return r;
                            const line = { ...r.line, turnoverForCommission: t };
                            return { ...r, line, computed: recomputeRow(r.employee, line) };
                        })
                    );
                } else {
                    setCrmInfo(null);
                    setCrmWarnings([]);
                    setCrmError("KeyCRM недоступний — ТО можна ввести вручну");
                }
            } catch {
                if (!cancelled) {
                    setCrmInfo(null);
                    setCrmWarnings([]);
                    setCrmError("KeyCRM: помилка мережі");
                }
            } finally {
                if (!cancelled) setCrmLoading(false);
            }

            setScheduleLoading(true);
            setScheduleError("");
            try {
                const schRes = await authFetch(
                    `/api/salary/schedule-sync?yearMonth=${encodeURIComponent(yearMonth)}`,
                    { credentials: "include" }
                );
                if (cancelled) return;
                if (schRes.ok) {
                    const schData = await schRes.json();
                    setScheduleInfo({
                        employeesMatched: schData.employeesMatched ?? 0,
                        employeesTotal: schData.employeesTotal ?? 0,
                    });
                    setScheduleWarnings(schData.warnings || []);
                    const counts = schData.counts || {};
                    setRows((prev) =>
                        prev.map((r) => {
                            const c = counts[String(r.employee._id)];
                            if (!c) return r;
                            const line = {
                                ...r.line,
                                shifts: c.shifts,
                                vacationDays: c.vacationDays,
                            };
                            return { ...r, line, computed: recomputeRow(r.employee, line) };
                        })
                    );
                } else {
                    setScheduleInfo(null);
                    setScheduleWarnings([]);
                    setScheduleError("Графік недоступний — зміни й відпустку можна ввести вручну");
                }
            } catch {
                if (!cancelled) {
                    setScheduleInfo(null);
                    setScheduleWarnings([]);
                    setScheduleError("Графік: помилка мережі");
                }
            } finally {
                if (!cancelled) setScheduleLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [yearMonth, applyMonthResponse]);

    const loadProfiles = useCallback(async () => {
        try {
            const res = await authFetch("/api/salary/employees/all", { credentials: "include" });
            if (res.ok) setAllProfiles(await res.json());
        } catch {
            /* ignore */
        }
    }, []);

    const loadUsers = useCallback(async () => {
        try {
            const res = await authFetch("/api/users", { credentials: "include" });
            if (res.ok) setUsers(await res.json());
        } catch {
            /* ignore */
        }
    }, []);

    useEffect(() => {
        if (tab === "profiles") {
            loadProfiles();
            loadUsers();
            loadFilterPresets();
        }
    }, [tab, loadProfiles, loadUsers, loadFilterPresets]);

    const updateLineLocal = (employeeId, patch) => {
        if (serverFinalized) return;
        setRows((prev) =>
            prev.map((r) => {
                if (String(r.employee._id) !== String(employeeId)) return r;
                const line = { ...r.line, ...patch };
                const computed = recomputeRow(r.employee, line);
                return { ...r, line, computed };
            })
        );
    };

    const recomputeRow = useCallback((employee, line) => {
        const turnover = Number(line.turnoverForCommission) || 0;
        const shifts = Number(line.shifts) || 0;
        const vacationDays = Number(line.vacationDays) || 0;
        const bonus = Number(line.bonus) || 0;
        const penalty = Number(line.penalty) || 0;
        let commission = 0;
        const ov = line.commissionAmountOverride;
        if (ov != null && ov !== "" && !Number.isNaN(Number(ov))) {
            commission = Math.round(Number(ov));
        } else if (employee.commissionEnabled && Number(employee.commissionPercent) > 0) {
            commission = Math.round(turnover * (Number(employee.commissionPercent) / 100));
        }
        const shiftPay = Math.round((Number(employee.ratePerShift) || 0) * shifts);
        const vacationPay = Math.round((Number(employee.vacationDayRate) || 0) * vacationDays);
        const base = Math.round(Number(employee.baseSalary) || 0);
        const accrued = base + commission + shiftPay + vacationPay + bonus - penalty;
        const hasAdvance = employee.hasAdvance !== false;
        const advance = hasAdvance ? Math.round(Number(employee.advanceAmount) || 0) : 0;
        const deduct = employee.deductAdvanceFromSalary !== false;
        const payOnSalaryDay = deduct ? accrued - advance : accrued;
        return {
            base,
            commission,
            shiftPay,
            vacationPay,
            bonus,
            penalty,
            accrued,
            advance,
            payOnSalaryDay,
            turnover,
            shifts,
            vacationDays,
        };
    }, []);

    const linesPayloadFromRows = (rowsArr) =>
        rowsArr.map((r) => ({
            employeeId: r.employee._id,
            turnoverForCommission: r.line.turnoverForCommission,
            shifts: r.line.shifts,
            vacationDays: r.line.vacationDays,
            bonus: r.line.bonus,
            penalty: r.line.penalty,
            commissionAmountOverride: r.line.commissionAmountOverride,
            notes: r.line.notes,
            paid: !!r.line.paid,
        }));

    const persistLinesQuiet = useCallback(
        async (rowsArr) => {
            if (serverFinalized) return;
            setPaidSaving(true);
            setError("");
            try {
                const res = await authFetch(`/api/salary/month/${yearMonth}`, {
                    method: "PUT",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ lines: linesPayloadFromRows(rowsArr) }),
                });
                if (!res.ok) {
                    const t = await res.text();
                    let msg = t || res.statusText;
                    try {
                        const j = JSON.parse(t);
                        if (j.error) msg = j.error;
                    } catch {
                        /* not JSON */
                    }
                    throw new Error(msg);
                }
                const data = await res.json();
                applyMonthResponse(data);
            } catch (e) {
                setError(e.message || "Не вдалося зберегти позначку");
                try {
                    const res = await authFetch(`/api/salary/month/${yearMonth}`, { credentials: "include" });
                    if (res.ok) applyMonthResponse(await res.json());
                } catch {
                    /* ignore */
                }
            } finally {
                setPaidSaving(false);
            }
        },
        [yearMonth, serverFinalized, applyMonthResponse]
    );

    const toggleLinePaid = async (employeeId, checked) => {
        if (serverFinalized) return;
        const next = rows.map((r) =>
            String(r.employee._id) === String(employeeId)
                ? { ...r, line: { ...r.line, paid: checked } }
                : r
        );
        setRows(next);
        await persistLinesQuiet(next);
    };

    const putMonth = async (body) => {
        setSaving(true);
        setError("");
        try {
            const res = await authFetch(`/api/salary/month/${yearMonth}`, {
                method: "PUT",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const t = await res.text();
                let msg = t || res.statusText;
                try {
                    const j = JSON.parse(t);
                    if (j.error) msg = j.error;
                } catch {
                    /* not JSON */
                }
                throw new Error(msg);
            }
            const data = await res.json();
            applyMonthResponse(data);
        } catch (e) {
            setError(e.message || "Не вдалося зберегти");
        } finally {
            setSaving(false);
        }
    };

    /** Чернетка: зберегти таблицю без фіксації (ТО з KeyCRM ще можна підтягнути). */
    const saveDraft = async () => {
        if (serverFinalized) return;
        if (
            !window.confirm(
                "Зберегти зміни в таблиці на сервер? Місяць лишається чернеткою — колонку «ТО для %» можна знову заповнити кнопкою «ТО з KeyCRM»."
            )
        ) {
            return;
        }
        await putMonth({ lines: linesPayloadFromRows(rows), finalized: false });
    };

    /** Фіксація місяця: знімок сум, без авто-оновлення ТО з CRM. */
    const finalizeMonthAllPaid = async () => {
        if (serverFinalized) return;
        if (
            !window.confirm(
                "Зафіксувати місяць як «усе виплачено»?\n\n" +
                    "• Усі суми в таблиці збережуться зі знімком (як зараз на екрані).\n" +
                    "• Зміни окладу / % у профілях на цей місяць більше не змінять ці числа.\n" +
                    "• ТО з KeyCRM для цього місяця автоматично не оновлюватимуться.\n\n" +
                    "Продовжити?"
            )
        ) {
            return;
        }
        await putMonth({ lines: linesPayloadFromRows(rows), finalized: true });
    };

    /** Після фіксації: знову дозволити редагування та ТО з CRM. */
    const unlockMonth = async () => {
        if (!serverFinalized) return;
        if (
            !window.confirm(
                "Розблокувати місяць? Після збереження можна знову змінювати таблицю та підтягувати ТО з KeyCRM."
            )
        ) {
            return;
        }
        await putMonth({ finalized: false });
    };

    const linePayloadForTelegram = (row) => ({
        turnoverForCommission: row.line.turnoverForCommission,
        shifts: row.line.shifts,
        vacationDays: row.line.vacationDays,
        bonus: row.line.bonus,
        penalty: row.line.penalty,
        commissionAmountOverride: row.line.commissionAmountOverride,
        notes: row.line.notes,
    });

    const tgModalBusy = Boolean(tgSendModal && (tgSendModal.loading || tgSendModal.sending));
    const tgSendActiveEmployeeId =
        tgSendModal && (tgSendModal.loading || tgSendModal.sending) ? tgSendModal.employeeId : null;

    const openSalaryTelegramSendModal = async (employeeId) => {
        const row = rows.find((r) => String(r.employee._id) === String(employeeId));
        if (!row) {
            setError("Рядок співробітника не знайдено");
            return;
        }
        if (!row.employee.linkedTelegramId) return;
        setError("");
        setTgSuccess("");
        const title = employeeDisplayName(row.employee);
        setTgSendModal({
            employeeId,
            title,
            text: "",
            chatId: null,
            loading: true,
            sending: false,
            loadError: "",
        });
        try {
            const res = await authFetch("/api/salary/preview-telegram", {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    yearMonth,
                    employeeId,
                    line: linePayloadForTelegram(row),
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || res.statusText);
            setTgSendModal((m) =>
                m && String(m.employeeId) === String(employeeId)
                    ? { ...m, loading: false, text: data.text || "", chatId: data.chatId ?? null }
                    : m
            );
        } catch (e) {
            setTgSendModal((m) =>
                m && String(m.employeeId) === String(employeeId)
                    ? { ...m, loading: false, loadError: e.message || "Не вдалося отримати текст" }
                    : m
            );
        }
    };

    const closeSalaryTelegramModal = () => {
        if (tgSendModal?.sending) return;
        setTgSendModal(null);
    };

    const confirmSendSalaryTelegram = async () => {
        const m = tgSendModal;
        if (!m || m.loading || m.loadError || m.sending) return;
        const row = rows.find((r) => String(r.employee._id) === String(m.employeeId));
        if (!row) {
            setTgSendModal(null);
            return;
        }
        setTgSendModal((prev) => (prev ? { ...prev, sending: true } : null));
        setError("");
        setTgSuccess("");
        try {
            const res = await authFetch("/api/salary/notify-telegram", {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    yearMonth,
                    employeeId: m.employeeId,
                    line: linePayloadForTelegram(row),
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || res.statusText);
            const sentAt = data.sentAt || new Date().toISOString();
            const mid = data.messageId;
            const chatId = data.chatId ?? m.chatId;
            setTgSendModal(null);
            const copyToSelf = data.copyToSelf;
            const copyNote = copyToSelf?.sent
                ? " Копію надіслано вам."
                : copyToSelf && copyToSelf.sent === false
                  ? ` Копію вам НЕ надіслано (${copyToSelf.reason || "помилка"}).`
                  : "";
            setTgSuccess(
                `Telegram прийняв повідомлення для «${m.title}» (chat_id ${chatId ?? "—"}${mid != null ? `, msg_id ${mid}` : ""}). Час: ${formatTgSentAt(sentAt)}.${copyNote} Статусу «прочитано» для ботів немає — за потреби уточніть у співробітника.`
            );
            window.setTimeout(() => setTgSuccess(""), 14000);
            setRows((prev) =>
                prev.map((row) =>
                    String(row.employee._id) === String(m.employeeId)
                        ? {
                              ...row,
                              canRevokeLastSalaryTelegram: true,
                              salaryTelegramMeta: {
                                  sentCount: (row.salaryTelegramMeta?.sentCount || 0) + 1,
                                  lastSentAt: sentAt,
                                  lastMessageId: mid ?? row.salaryTelegramMeta?.lastMessageId ?? null,
                              },
                          }
                        : row
                )
            );
        } catch (e) {
            setTgSendModal((prev) => (prev ? { ...prev, sending: false } : null));
            setError(e.message || "Не вдалося надіслати в Telegram");
        }
    };

    const revokeLastSalaryTelegram = async (employeeId) => {
        if (
            !window.confirm(
                "Видалити останнє з надісланих повідомлень ЗП у Telegram? Якщо надіслали кілька разів підряд — можна натискати знову, поки стек не спорожніє.\n\n" +
                    "Telegram часто дозволяє видаляти лише недавні повідомлення бота (до ~48 год). Якщо не вийде — видаліть вручну в чаті."
            )
        ) {
            return;
        }
        setTgRevokingId(employeeId);
        setError("");
        setTgSuccess("");
        try {
            const res = await authFetch("/api/salary/revoke-last-telegram", {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ yearMonth, employeeId }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || res.statusText);
            const left = Number(data.remainingSalaryTgMessages) || 0;
            setTgSuccess(
                left > 0
                    ? `Повідомлення видалено. Ще можна видалити ${left} з надісланих (натисніть кнопку з іконкою кошика знову).`
                    : "Останнє надіслане повідомлення ЗП у Telegram прибрано зі стека."
            );
            window.setTimeout(() => setTgSuccess(""), 8000);
            setRows((prev) =>
                prev.map((row) => {
                    if (String(row.employee._id) !== String(employeeId)) return row;
                    if (left <= 0) {
                        return { ...row, canRevokeLastSalaryTelegram: false, salaryTelegramMeta: null };
                    }
                    return {
                        ...row,
                        canRevokeLastSalaryTelegram: true,
                        salaryTelegramMeta: row.salaryTelegramMeta
                            ? {
                                  ...row.salaryTelegramMeta,
                                  sentCount: left,
                                  lastMessageId: null,
                              }
                            : { sentCount: left, lastSentAt: null, lastMessageId: null },
                    };
                })
            );
        } catch (e) {
            setError(e.message || "Не вдалося видалити повідомлення в Telegram");
        } finally {
            setTgRevokingId(null);
        }
    };

    const refreshCrmTurnoversIntoRows = useCallback(async (opts) => {
        if (!opts?.ignoreFinalized && serverFinalized) {
            window.alert(
                "Місяць зафіксовано після виплати. Натисніть «Розблокувати місяць» зверху — після цього можна знову оновити ТО з KeyCRM."
            );
            return;
        }
        setCrmLoading(true);
        setCrmError("");
        try {
            const crmRes = await authFetch("/api/salary/crm-sync/apply", {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ yearMonth }),
            });
            if (crmRes.ok) {
                const data = await crmRes.json();
                applyMonthResponse(data);
                setCrmInfo({ ordersCount: data.crm?.ordersCount ?? 0 });
                setCrmWarnings(data.crm?.warnings || []);
            } else {
                const errData = await crmRes.json().catch(() => ({}));
                setCrmInfo(null);
                setCrmWarnings([]);
                setCrmError(errData.error || "KeyCRM недоступний");
            }
        } catch {
            setCrmInfo(null);
            setCrmWarnings([]);
            setCrmError("KeyCRM: помилка мережі");
        } finally {
            setCrmLoading(false);
        }
    }, [yearMonth, applyMonthResponse, serverFinalized]);

    const parseCsvIds = (str) => {
        if (!str || typeof str !== "string") return [];
        return str
            .split(/[,;\s]+/)
            .map((s) => s.trim())
            .filter(Boolean)
            .map((s) => parseInt(s, 10))
            .filter((n) => !Number.isNaN(n));
    };

    /** user — необовʼязково: створення профілю для конкретного користувача з вкладки «Профілі ЗП». */
    const openNewProfile = (user = null) => {
        loadFilterPresets();
        setEditForm({
            _id: null,
            displayName: user ? user.profile?.lastName || user.username || "" : "",
            groupLabel: "",
            sortOrder: 100,
            active: true,
            userId: user?._id || "",
            crmManagerIdOverride: "",
            filterPresetId: "",
            hasAdvance: true,
            advanceAmount: 0,
            advanceDayOfMonth: 15,
            salaryDayOfMonth: 1,
            baseSalary: 0,
            commissionEnabled: false,
            commissionPercent: 0,
            ratePerShift: 0,
            vacationDayRate: 0,
            commissionNote: "",
            deductAdvanceFromSalary: true,
            conditionsEffectiveFrom: ymNow(),
        });
        setEditOpen(true);
    };

    const openEditProfile = (p) => {
        loadFilterPresets();
        const uid = p.userId && typeof p.userId === "object" ? p.userId._id : p.userId;
        setEditForm({
            ...p,
            userId: uid || "",
            hasAdvance: p.hasAdvance !== false,
            commissionEnabled: p.commissionEnabled === true,
            active: p.active !== false,
            deductAdvanceFromSalary: p.deductAdvanceFromSalary !== false,
            crmManagerIdOverride:
                p.crmManagerIdOverride != null && !Number.isNaN(Number(p.crmManagerIdOverride))
                    ? String(p.crmManagerIdOverride)
                    : "",
            filterPresetId: p.filterPresetId ? String(p.filterPresetId) : "",
            conditionsEffectiveFrom: ymNow(),
        });
        setEditOpen(true);
    };

    const saveProfile = async () => {
        if (!editForm) return;
        const payload = {
            displayName: editForm.displayName,
            groupLabel: editForm.groupLabel,
            sortOrder: Number(editForm.sortOrder) || 0,
            active: editForm.active !== false,
            userId: editForm.userId || null,
            crmManagerIdOverride:
                editForm.crmManagerIdOverride === "" || editForm.crmManagerIdOverride == null
                    ? null
                    : Number(editForm.crmManagerIdOverride),
            filterPresetId: editForm.filterPresetId || null,
            crmStatusIds: [],
            crmTagIncludeIds: [],
            crmTagExcludeIds: [],
            crmTagIncludeMode: "any",
            hasAdvance: editForm.hasAdvance !== false,
            advanceAmount: Number(editForm.advanceAmount) || 0,
            advanceDayOfMonth: Number(editForm.advanceDayOfMonth) || 15,
            salaryDayOfMonth: Number(editForm.salaryDayOfMonth) || 1,
            baseSalary: Number(editForm.baseSalary) || 0,
            commissionEnabled: editForm.commissionEnabled === true,
            commissionPercent: Number(editForm.commissionPercent) || 0,
            ratePerShift: Number(editForm.ratePerShift) || 0,
            vacationDayRate: Number(editForm.vacationDayRate) || 0,
            qualifyingStatuses: [],
            commissionNote: editForm.commissionNote || "",
            deductAdvanceFromSalary: editForm.deductAdvanceFromSalary !== false,
            conditionsEffectiveFrom: editForm.conditionsEffectiveFrom || ymNow(),
        };
        try {
            if (editForm._id) {
                const res = await authFetch(`/api/salary/employees/${editForm._id}`, {
                    method: "PUT",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });
                if (!res.ok) throw new Error(await res.text());
            } else {
                const res = await authFetch("/api/salary/employees", {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });
                if (!res.ok) throw new Error(await res.text());
            }
            setEditOpen(false);
            setEditForm(null);
            await loadProfiles();
            const mr = await authFetch(`/api/salary/month/${yearMonth}`, { credentials: "include" });
            if (mr.ok) {
                const md = await mr.json();
                applyMonthResponse(md);
                if (!md.finalized) await refreshCrmTurnoversIntoRows({ ignoreFinalized: true });
            }
        } catch (e) {
            setError(e.message || "Помилка збереження профілю");
        }
    };

    /** Видалити запис історії умов ЗП; місяці з нього рахуватимуться за попереднім записом. */
    const deleteConditionsEntry = async (effectiveFrom) => {
        if (!editForm?._id) return;
        if (!window.confirm(`Видалити умови, що діють з ${effectiveFrom}?`)) return;
        try {
            const res = await authFetch(`/api/salary/employees/${editForm._id}/conditions/${effectiveFrom}`, {
                method: "DELETE",
                credentials: "include",
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
            setEditForm((prev) => (prev ? { ...prev, conditionsHistory: data.conditionsHistory || [] } : prev));
            await loadProfiles();
            const mr = await authFetch(`/api/salary/month/${yearMonth}`, { credentials: "include" });
            if (mr.ok) applyMonthResponse(await mr.json());
        } catch (e) {
            setError(e.message || "Помилка видалення запису історії");
        }
    };

    /** Підставити умови з запису історії у форму, щоб відредагувати саме його. */
    const editConditionsEntry = (h) => {
        setEditForm((prev) =>
            prev
                ? {
                      ...prev,
                      conditionsEffectiveFrom: h.effectiveFrom,
                      baseSalary: h.baseSalary ?? 0,
                      commissionEnabled: h.commissionEnabled === true,
                      commissionPercent: h.commissionPercent ?? 0,
                      ratePerShift: h.ratePerShift ?? 0,
                      vacationDayRate: h.vacationDayRate ?? 0,
                      hasAdvance: h.hasAdvance !== false,
                      advanceAmount: h.advanceAmount ?? 0,
                      deductAdvanceFromSalary: h.deductAdvanceFromSalary !== false,
                  }
                : prev
        );
    };

    const deactivateProfile = async (id) => {
        if (!window.confirm("Приховати співробітника з розрахунків?")) return;
        await authFetch(`/api/salary/employees/${id}`, { method: "DELETE", credentials: "include" });
        await loadProfiles();
        const mr = await authFetch(`/api/salary/month/${yearMonth}`, { credentials: "include" });
        if (mr.ok) {
            const md = await mr.json();
            applyMonthResponse(md);
            if (!md.finalized) await refreshCrmTurnoversIntoRows({ ignoreFinalized: true });
        }
    };

    /**
     * Вкладка «Профілі ЗП»: усі активні користувачі, крім таргетолога (з профілем або без нього),
     * плюс увімкнені профілі без привʼязаного користувача. Звільнені й вимкнені — лише з галочкою.
     */
    const profileRows = useMemo(() => {
        if (showAllProfiles) return allProfiles.map((p) => ({ profile: p }));
        const usersById = new Map(users.map((u) => [String(u._id), u]));
        const isListedUser = (u) => u && !u.blocked && u.role !== "targetolog";
        const linkedUserIds = new Set();
        const rows = [];
        for (const p of allProfiles) {
            if (!p.userId) {
                if (p.active) rows.push({ profile: p });
                continue;
            }
            linkedUserIds.add(String(p.userId));
            const u = usersById.get(String(p.userId));
            if (u ? isListedUser(u) : p.active) rows.push({ profile: p });
        }
        for (const u of users) {
            if (isListedUser(u) && !linkedUserIds.has(String(u._id))) rows.push({ user: u });
        }
        return rows;
    }, [allProfiles, users, showAllProfiles]);

    const userOptions = useMemo(() => {
        return users.map((u) => ({
            value: u._id,
            label: [u.profile?.firstName, u.profile?.lastName].filter(Boolean).join(" ") || u.username,
            keycrmManagerId: u.profile?.keycrmManagerId,
        }));
    }, [users]);

    const openAddRef = (kind) => {
        setRefKind(kind);
        setRefForm({ _id: null, title: "", crmId: "", colorHex: "" });
        setRefModalOpen(true);
    };

    const openEditRef = (row) => {
        setRefKind(row.kind);
        setRefForm({
            _id: row._id,
            title: row.title,
            crmId: String(row.crmId),
            colorHex: row.colorHex || "",
        });
        setRefModalOpen(true);
    };

    const saveRefRecord = async () => {
        const body = {
            kind: refKind,
            title: refForm.title.trim(),
            crmId: Number(refForm.crmId),
            colorHex: refForm.colorHex != null ? String(refForm.colorHex).trim() : "",
        };
        if (!body.title || Number.isNaN(body.crmId)) return;
        const url = refForm._id
            ? `/api/salary/crm-reference/${refForm._id}`
            : "/api/salary/crm-reference";
        const method = refForm._id ? "PUT" : "POST";
        const res = await authFetch(url, {
            method,
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
        if (res.ok) {
            await loadCrmReference();
            setRefModalOpen(false);
        }
    };

    const deleteRefRecord = async (id, meta = {}) => {
        const kindLabel = meta.kind === "order_tag" ? "тег" : "статус";
        const label = meta.title ? ` «${meta.title}»` : "";
        if (!window.confirm(`Видалити ${kindLabel}${label} з довідника?`)) return false;
        const res = await authFetch(`/api/salary/crm-reference/${id}`, { method: "DELETE", credentials: "include" });
        if (res.ok) {
            await loadCrmReference();
            return true;
        }
        return false;
    };

    const openNewPreset = () => {
        setPresetForm({
            _id: null,
            name: "",
            statusIdsText: "",
            tagIncludeIdsText: "",
            tagExcludeIdsText: "",
            tagIncludeMode: "any",
        });
        setPresetModalOpen(true);
    };

    const openEditPreset = (p) => {
        setPresetForm({
            _id: p._id,
            name: p.name || "",
            statusIdsText: numsToCsv(p.statusIds),
            tagIncludeIdsText: numsToCsv(p.tagIncludeIds),
            tagExcludeIdsText: numsToCsv(p.tagExcludeIds),
            tagIncludeMode: p.tagIncludeMode === "all" ? "all" : "any",
        });
        setPresetModalOpen(true);
    };

    const savePreset = async () => {
        if (!presetForm?.name?.trim()) return;
        const body = {
            name: presetForm.name.trim(),
            statusIds: parseCsvIds(presetForm.statusIdsText),
            tagIncludeIds: parseCsvIds(presetForm.tagIncludeIdsText),
            tagExcludeIds: parseCsvIds(presetForm.tagExcludeIdsText),
            tagIncludeMode: presetForm.tagIncludeMode === "all" ? "all" : "any",
        };
        const url = presetForm._id
            ? `/api/salary/filter-presets/${presetForm._id}`
            : "/api/salary/filter-presets";
        const method = presetForm._id ? "PUT" : "POST";
        const res = await authFetch(url, {
            method,
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
        if (res.ok) {
            await loadFilterPresets();
            setPresetModalOpen(false);
        }
    };

    const deletePreset = async (id) => {
        if (!window.confirm("Видалити набір?")) return;
        await authFetch(`/api/salary/filter-presets/${id}`, { method: "DELETE", credentials: "include" });
        loadFilterPresets();
    };

    const roleOk = user && user.role === "admin";
    if (!roleOk) {
        return (
            <div className="container py-5">
                <Alert variant="warning">Немає доступу до розділу зарплат.</Alert>
            </div>
        );
    }

    return (
        <div className="container-fluid py-3 px-2 px-md-3 salaries-page">
            <div className="d-flex align-items-center justify-content-between gap-2 mb-3 flex-wrap">
                <h1 className="h3 mb-0">Зарплати</h1>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    className="d-flex align-items-center gap-1 salaries-tools-btn"
                    title="Довідники, набори фільтрів, аванс, профілі ЗП"
                    onClick={() => setSalariesToolsOpen(true)}
                >
                    <Gear size={18} aria-hidden />
                    <span className="d-none d-sm-inline">Налаштування</span>
                </Button>
            </div>
            <Alert variant="info" className="small py-2 mb-3">
                Розшифровка ЗП у особисті йде в <strong>Telegram</strong> (бот), не в SMS. Спочатку відкриється вікно з
                текстом — після підтвердження повідомлення летить у чат за <code>Telegram ID</code> із профілю
                користувача. Зелений алерт після відправки означає, що API Telegram прийняв повідомлення; статусу
                «прочитано» там немає.
            </Alert>
            <p className="text-muted small mb-4 d-none d-md-block">
                <strong>Зберегти чернетку</strong> — записати таблицю на сервер без фіксації; при відкритті місяця ТО знову
                можна підтягнути з KeyCRM кнопкою нижче, суми перераховуються з профілів. <strong>ТО з KeyCRM</strong> —
                підставляє обороти в колонку «ТО для %» за фільтрами з профілю ЗП (не змінює інші поля).{" "}
                <strong>Усе виплачено</strong> — зафіксувати місяць: знімок усіх сум, без авто-оновлення ТО та без впливу
                змін профілю на цей місяць; далі лише «Розблокувати місяць», щоб знову редагувати. Колонка{" "}
                <strong>Оплачено</strong> — хто вже отримав гроші (зелений рядок); такі суми не входять у «Залишок до
                виплати» зверху. <strong>Надіслати в Telegram</strong> — переглянути текст і надіслати розшифровку (потрібен
                Telegram ID у обліковому записі). У колонці показано час останньої відправки.{" "}
                <strong>Іконка кошика</strong> — прибрати останнє з черги надісланих (до ~48 год, якщо Telegram
                дозволяє).
            </p>
            <p className="text-muted small mb-4 d-md-none">
                Чернетка / «Усе виплачено» / ТО з KeyCRM / картки співробітників нижче. Повний текст підказок — на ширшому
                екрані.
            </p>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError("")}>
                    {error}
                </Alert>
            )}
            {tgSuccess && (
                <Alert variant="success" dismissible onClose={() => setTgSuccess("")}>
                    {tgSuccess}
                </Alert>
            )}

            <Tabs activeKey={tab} onSelect={(k) => setTab(k || "desk")} className="mb-3 salaries-month-tabs">
                <Tab eventKey="desk" title="Розрахунок">
                    <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
                        <Form.Group style={{ maxWidth: 220 }}>
                            <Form.Label>Місяць</Form.Label>
                            <AppMonthField value={yearMonth} onChange={setYearMonth} />
                        </Form.Group>
                        <Button
                            variant="primary"
                            onClick={saveDraft}
                            disabled={saving || loading || serverFinalized}
                            title={
                                serverFinalized
                                    ? "Спочатку розблокуйте місяць"
                                    : "Зберегти таблицю без фіксації (є підтвердження)"
                            }
                        >
                            {saving ? <Spinner animation="border" size="sm" /> : "Зберегти чернетку"}
                        </Button>
                        <Button
                            variant="success"
                            onClick={finalizeMonthAllPaid}
                            disabled={saving || loading || serverFinalized}
                            title="Зафіксувати місяць зі знімком сум (є підтвердження)"
                        >
                            {saving ? <Spinner animation="border" size="sm" /> : "Усе виплачено"}
                        </Button>
                        {serverFinalized && (
                            <Button
                                variant="outline-warning"
                                onClick={unlockMonth}
                                disabled={saving || loading}
                                title="Зняти фіксацію, щоб знову редагувати та тягнути ТО з KeyCRM"
                            >
                                {saving ? <Spinner animation="border" size="sm" /> : "Розблокувати місяць"}
                            </Button>
                        )}
                        <Button
                            variant="outline-secondary"
                            className="salaries-crm-sync-btn"
                            onClick={() => refreshCrmTurnoversIntoRows()}
                            disabled={crmLoading || loading || serverFinalized}
                            title="Завантажити з KeyCRM обороти менеджерів у колонку «ТО для %» (за фільтрами профілю). Не змінює премії, зміни тощо."
                        >
                            {crmLoading ? <Spinner animation="border" size="sm" /> : "ТО з KeyCRM"}
                        </Button>
                        {scheduleLoading && (
                            <span className="small text-muted align-self-center d-flex align-items-center gap-1">
                                <Spinner animation="border" size="sm" />
                                Графік…
                            </span>
                        )}
                        {paidSaving && (
                            <span className="small text-muted align-self-center d-flex align-items-center gap-1">
                                <Spinner animation="border" size="sm" />
                                Позначки оплати…
                            </span>
                        )}
                    </div>

                    {!loading && rows.length > 0 && (
                        <Alert variant="light" className="py-2 small border mb-3">
                            Виплата по черзі: оплачено{" "}
                            <strong>
                                {paidProgress.done}/{paidProgress.total}
                            </strong>
                            . Кнопка «Оплачено» у рядку робить його зеленим; сума цього рядка не входить у «Залишок до
                            виплати» зверху. Коли всі отримали гроші й таблиця готова — «Усе виплачено», щоб зафіксувати
                            місяць.
                        </Alert>
                    )}

                    {serverFinalized && (
                        <Alert variant="success" className="py-2 small mb-3">
                            Місяць зафіксовано («Усе виплачено»)
                            {monthFinalizedAt
                                ? ` (${new Date(monthFinalizedAt).toLocaleString("uk-UA")})`
                                : ""}
                            . Рядки лише для перегляду: суми за знімком. Щоб знову редагувати — «Розблокувати місяць».
                        </Alert>
                    )}

                    {crmInfo && (
                        <Alert variant="info" className="py-2 small">
                            KeyCRM: завантажено <strong>{crmInfo.ordersCount}</strong> замовлень за місяць — колонку «ТО
                            для %» оновлено за фільтрами профілів.
                        </Alert>
                    )}
                    {crmError && (
                        <Alert variant="warning" className="py-2 small">
                            {crmError}
                        </Alert>
                    )}
                    {crmWarnings?.length > 0 && (
                        <Alert variant="secondary" className="py-2 small">
                            Немає KeyCRM manager id (додайте в користувача або перевизначення):{" "}
                            {crmWarnings.map((w) => w.displayName).join(", ")}
                        </Alert>
                    )}

                    {scheduleInfo && (
                        <Alert variant="info" className="py-2 small">
                            Графік: підставлено «Зміни» і «Відпустка, дн» для{" "}
                            <strong>
                                {scheduleInfo.employeesMatched}/{scheduleInfo.employeesTotal}
                            </strong>{" "}
                            співробітників.
                        </Alert>
                    )}
                    {scheduleError && (
                        <Alert variant="warning" className="py-2 small">
                            {scheduleError}
                        </Alert>
                    )}
                    {scheduleWarnings?.length > 0 && (
                        <Alert variant="secondary" className="py-2 small">
                            Без даних з графіка (немає прив&apos;язаного користувача або відміток за місяць — залишено
                            0):{" "}
                            {scheduleWarnings.map((w) => w.displayName).join(", ")}
                        </Alert>
                    )}

                    {loading ? (
                        <Spinner animation="border" />
                    ) : (
                        <>
                            <div className="row g-3 mb-4">
                                <div className="col-12 col-md-4">
                                    <div className="border rounded p-3 bg-light">
                                        <div className="small text-muted">Сума авансів (день авансу)</div>
                                        <div className="h4 mb-0">{formatMoney(derivedTotals.advanceTotal)}</div>
                                    </div>
                                </div>
                                <div className="col-12 col-md-4">
                                    <div className="border rounded p-3 bg-light">
                                        <div className="small text-muted">Усього нараховано за місяць</div>
                                        <div className="h4 mb-0">{formatMoney(derivedTotals.accruedTotal)}</div>
                                    </div>
                                </div>
                                <div className="col-12 col-md-4">
                                    <div className="border rounded p-3 bg-light">
                                        <div className="small text-muted">Залишок до виплати 1-го</div>
                                        <div className="h4 mb-0">{formatMoney(derivedTotals.salaryDayRemaining)}</div>
                                        {paidProgress.done > 0 && (
                                            <div className="small text-muted mt-1">
                                                Усі рядки до 1-го разом: {formatMoney(derivedTotals.salaryDayTotal)} грн
                                                (оплачені рядки з залишку вже відняті)
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="d-lg-none">
                                {rows.map((r) => (
                                    <SalariesEmployeeCard
                                        key={r.employee._id}
                                        r={r}
                                        serverFinalized={serverFinalized}
                                        paidSaving={paidSaving}
                                        tgModalBusy={tgModalBusy}
                                        tgSendActiveEmployeeId={tgSendActiveEmployeeId}
                                        tgRevokingId={tgRevokingId}
                                        updateLineLocal={updateLineLocal}
                                        toggleLinePaid={toggleLinePaid}
                                        openSalaryTelegramSendModal={openSalaryTelegramSendModal}
                                        revokeLastSalaryTelegram={revokeLastSalaryTelegram}
                                        employeeDisplayName={employeeDisplayName}
                                        formatMoney={formatMoney}
                                        formatTgSentAt={formatTgSentAt}
                                    />
                                ))}
                            </div>

                            <div className="table-responsive d-none d-lg-block">
                                <Table striped bordered hover size="sm" className="align-middle">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>Співробітник</th>
                                            <th>Група</th>
                                            <th>ТО для %</th>
                                            <th>Зміни</th>
                                            <th>Відпустка, дн</th>
                                            <th>Премія</th>
                                            <th>Штраф</th>
                                            <th>Комісія вручну</th>
                                            <th>Комісія</th>
                                            <th>Зміни грн</th>
                                            <th>Відпускні грн</th>
                                            <th>Нараховано</th>
                                            <th>Аванс</th>
                                            <th>До виплати 1-го</th>
                                            <th style={{ width: 108 }} className="text-center">
                                                Оплачено
                                            </th>
                                            <th style={{ minWidth: 168 }}>Telegram</th>
                                            <th>Нотатка</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r) => (
                                            <tr key={r.employee._id} className={r.line.paid ? "table-success" : undefined}>
                                                <td>
                                                    <strong>{employeeDisplayName(r.employee)}</strong>
                                                    {r.employee.linkedName && (
                                                        <div className="small text-muted">
                                                            профіль: {r.employee.displayName}
                                                        </div>
                                                    )}
                                                    {r.employee.commissionNote && (
                                                        <div className="small text-muted">{r.employee.commissionNote}</div>
                                                    )}
                                                </td>
                                                <td>{r.employee.groupLabel}</td>
                                                <td style={{ minWidth: 100 }}>
                                                    <Form.Control
                                                        type="number"
                                                        min={0}
                                                        disabled={serverFinalized}
                                                        value={r.line.turnoverForCommission}
                                                        onChange={(e) =>
                                                            updateLineLocal(r.employee._id, {
                                                                turnoverForCommission: e.target.value,
                                                            })
                                                        }
                                                        size="sm"
                                                    />
                                                </td>
                                                <td style={{ width: 80 }}>
                                                    <Form.Control
                                                        type="number"
                                                        min={0}
                                                        disabled={serverFinalized}
                                                        value={r.line.shifts}
                                                        onChange={(e) =>
                                                            updateLineLocal(r.employee._id, { shifts: e.target.value })
                                                        }
                                                        size="sm"
                                                    />
                                                </td>
                                                <td style={{ width: 90 }}>
                                                    <Form.Control
                                                        type="number"
                                                        min={0}
                                                        disabled={serverFinalized}
                                                        value={r.line.vacationDays}
                                                        onChange={(e) =>
                                                            updateLineLocal(r.employee._id, {
                                                                vacationDays: e.target.value,
                                                            })
                                                        }
                                                        size="sm"
                                                    />
                                                </td>
                                                <td style={{ width: 90 }}>
                                                    <Form.Control
                                                        type="number"
                                                        disabled={serverFinalized}
                                                        value={r.line.bonus}
                                                        onChange={(e) =>
                                                            updateLineLocal(r.employee._id, { bonus: e.target.value })
                                                        }
                                                        size="sm"
                                                    />
                                                </td>
                                                <td style={{ width: 90 }}>
                                                    <Form.Control
                                                        type="number"
                                                        disabled={serverFinalized}
                                                        value={r.line.penalty}
                                                        onChange={(e) =>
                                                            updateLineLocal(r.employee._id, { penalty: e.target.value })
                                                        }
                                                        size="sm"
                                                    />
                                                </td>
                                                <td style={{ width: 100 }}>
                                                    <Form.Control
                                                        type="number"
                                                        placeholder="авто"
                                                        disabled={serverFinalized}
                                                        value={
                                                            r.line.commissionAmountOverride == null
                                                                ? ""
                                                                : r.line.commissionAmountOverride
                                                        }
                                                        onChange={(e) =>
                                                            updateLineLocal(r.employee._id, {
                                                                commissionAmountOverride:
                                                                    e.target.value === "" ? null : e.target.value,
                                                            })
                                                        }
                                                        size="sm"
                                                    />
                                                </td>
                                                <td>{r.computed.commission}</td>
                                                <td>{r.computed.shiftPay}</td>
                                                <td>{r.computed.vacationPay}</td>
                                                <td>
                                                    <strong>{r.computed.accrued}</strong>
                                                </td>
                                                <td>
                                                    {r.employee.hasAdvance === false ? (
                                                        <span className="text-muted">—</span>
                                                    ) : (
                                                        <>
                                                            {r.computed.advance}
                                                            <div className="small text-muted">
                                                                {r.employee.advanceDayOfMonth}-го
                                                            </div>
                                                        </>
                                                    )}
                                                </td>
                                                <td>
                                                    {r.line.paid ? (
                                                        <>
                                                            <span className="text-muted text-decoration-line-through">
                                                                {r.computed.payOnSalaryDay}
                                                            </span>
                                                            <div className="small text-success fw-semibold">у залишку 0</div>
                                                            <div className="small text-muted">
                                                                {r.employee.salaryDayOfMonth}-го
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <strong className="text-success">
                                                                {r.computed.payOnSalaryDay}
                                                            </strong>
                                                            <div className="small text-muted">
                                                                {r.employee.salaryDayOfMonth}-го
                                                            </div>
                                                        </>
                                                    )}
                                                </td>
                                                <td className="text-center align-middle">
                                                    <Button
                                                        variant={r.line.paid ? "outline-secondary" : "outline-success"}
                                                        size="sm"
                                                        className="py-0 px-2"
                                                        disabled={serverFinalized || paidSaving}
                                                        title={
                                                            serverFinalized
                                                                ? "Розблокуйте місяць, щоб змінювати позначки"
                                                                : r.line.paid
                                                                  ? "Повернути суму в залишок до виплати"
                                                                  : "Позначити виплату (рядок стане зеленим, сума не в залишку)"
                                                        }
                                                        onClick={() => toggleLinePaid(r.employee._id, !r.line.paid)}
                                                    >
                                                        {r.line.paid ? "Скасувати" : "Оплачено"}
                                                    </Button>
                                                </td>
                                                <td className="align-middle">
                                                    <div className="d-flex flex-column gap-1">
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            className="py-0"
                                                            disabled={
                                                                !r.employee.linkedTelegramId ||
                                                                tgModalBusy ||
                                                                Boolean(tgRevokingId) ||
                                                                paidSaving
                                                            }
                                                            title={
                                                                !r.employee.linkedTelegramId
                                                                    ? "Прив'яжіть користувача в профілі ЗП і вкажіть Telegram ID у обліковому записі користувача"
                                                                    : "Спочатку перегляньте текст, потім надішліть у Telegram (не SMS)"
                                                            }
                                                            onClick={() => openSalaryTelegramSendModal(r.employee._id)}
                                                        >
                                                            {tgSendActiveEmployeeId &&
                                                            String(tgSendActiveEmployeeId) === String(r.employee._id) ? (
                                                                <Spinner animation="border" size="sm" />
                                                            ) : (
                                                                "Надіслати в Telegram"
                                                            )}
                                                        </Button>
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            className="py-0 px-2 d-inline-flex align-items-center justify-content-center"
                                                            disabled={
                                                                !r.canRevokeLastSalaryTelegram ||
                                                                tgModalBusy ||
                                                                Boolean(tgRevokingId) ||
                                                                paidSaving
                                                            }
                                                            title={
                                                                r.canRevokeLastSalaryTelegram
                                                                    ? "Видалити останнє з надісланих (LIFO). Повторіть, щоб прибрати всі підряд"
                                                                    : "Спочатку надішліть розрахунок кнопкою «Надіслати в Telegram»"
                                                            }
                                                            aria-label="Видалити останнє повідомлення ЗП у Telegram"
                                                            onClick={() => revokeLastSalaryTelegram(r.employee._id)}
                                                        >
                                                            {tgRevokingId === r.employee._id ? (
                                                                <Spinner animation="border" size="sm" />
                                                            ) : (
                                                                <Trash size={16} aria-hidden />
                                                            )}
                                                        </Button>
                                                        {r.salaryTelegramMeta?.lastSentAt ? (
                                                            <div className="small text-muted mt-1 text-start">
                                                                Останнє: {formatTgSentAt(r.salaryTelegramMeta.lastSentAt)}
                                                                {r.salaryTelegramMeta.sentCount > 1
                                                                    ? ` · у стеку ${r.salaryTelegramMeta.sentCount}`
                                                                    : ""}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                </td>
                                                <td style={{ minWidth: 120 }}>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={1}
                                                        disabled={serverFinalized}
                                                        value={r.line.notes}
                                                        onChange={(e) =>
                                                            updateLineLocal(r.employee._id, { notes: e.target.value })
                                                        }
                                                        size="sm"
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </>
                    )}
                </Tab>

                <Tab eventKey="crm-dict" title="Довідник CRM">
                    <div className="d-flex gap-2 mb-3">
                        <Button size="sm" onClick={() => openAddRef("order_status")}>
                            + Статус
                        </Button>
                        <Button size="sm" onClick={() => openAddRef("order_tag")}>
                            + Тег
                        </Button>
                    </div>
                    <p className="small text-muted mb-3">
                        Статуси та теги додаються вручну (назва, id у KeyCRM, колір). Далі вони входять до наборів
                        фільтрів; у профілі ЗП обирається лише набір. Синхронізація ТО з CRM — на вкладці «Розрахунок».
                    </p>
                    <h6 className="mt-2">Статуси замовлень (назва — id)</h6>
                    <Table striped bordered size="sm" className="mb-4">
                        <thead>
                            <tr>
                                <th>Назва</th>
                                <th>ID у CRM</th>
                                <th style={{ width: 200 }}>Дії</th>
                            </tr>
                        </thead>
                        <tbody>
                            {crmStatusesSorted.map((row) => (
                                <tr key={row._id}>
                                    <td>
                                        <CrmColoredBadge label={row.title} colorHex={row.colorHex} />
                                    </td>
                                    <td>{row.crmId}</td>
                                    <td className="text-nowrap">
                                        <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openEditRef(row)}>
                                            Змінити
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline-danger"
                                            title="Видалити статус"
                                            aria-label={`Видалити статус ${row.title}`}
                                            onClick={() => deleteRefRecord(row._id, { kind: "order_status", title: row.title })}
                                        >
                                            <Trash className="me-1" aria-hidden />
                                            Видалити
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <h6 className="mt-4">Теги (назва — id)</h6>
                    <Table striped bordered size="sm">
                        <thead>
                            <tr>
                                <th>Назва</th>
                                <th>ID у CRM</th>
                                <th style={{ width: 200 }}>Дії</th>
                            </tr>
                        </thead>
                        <tbody>
                            {crmTagsSorted.map((row) => (
                                <tr key={row._id}>
                                    <td>
                                        <CrmColoredBadge label={row.title} colorHex={row.colorHex} />
                                    </td>
                                    <td>{row.crmId}</td>
                                    <td className="text-nowrap">
                                        <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openEditRef(row)}>
                                            Змінити
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline-danger"
                                            title="Видалити тег"
                                            aria-label={`Видалити тег ${row.title}`}
                                            onClick={() => deleteRefRecord(row._id, { kind: "order_tag", title: row.title })}
                                        >
                                            <Trash className="me-1" aria-hidden />
                                            Видалити
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Tab>

                <Tab eventKey="presets" title="Набори фільтрів">
                    <div className="mb-3">
                        <Button onClick={openNewPreset}>Новий набір</Button>
                    </div>
                    <Table striped bordered size="sm">
                        <thead className="table-dark">
                            <tr>
                                <th>Назва</th>
                                <th>Статуси (id)</th>
                                <th>Теги вкл.</th>
                                <th>Теги викл.</th>
                                <th>Режим тегів</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filterPresets.map((p) => (
                                <tr key={p._id}>
                                    <td>{p.name}</td>
                                    <td className="small">{(p.statusIds || []).join(", ") || "—"}</td>
                                    <td className="small">{(p.tagIncludeIds || []).join(", ") || "—"}</td>
                                    <td className="small">{(p.tagExcludeIds || []).join(", ") || "—"}</td>
                                    <td>{p.tagIncludeMode === "all" ? "усі теги" : "хоча б один"}</td>
                                    <td>
                                        <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openEditPreset(p)}>
                                            Змінити
                                        </Button>
                                        <Button size="sm" variant="outline-danger" onClick={() => deletePreset(p._id)}>
                                            Видалити
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Tab>

                <Tab eventKey="advance" title="Лише аванс">
                    {loading ? (
                        <Spinner animation="border" />
                    ) : (
                        <Table striped bordered size="sm">
                            <thead className="table-secondary">
                                <tr>
                                    <th>Співробітник</th>
                                    <th>День</th>
                                    <th>Сума</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows
                                    .filter((r) => r.employee.hasAdvance !== false)
                                    .map((r) => (
                                        <tr key={r.employee._id}>
                                            <td>{employeeDisplayName(r.employee)}</td>
                                            <td>{r.employee.advanceDayOfMonth}</td>
                                            <td>{formatMoney(r.computed.advance)}</td>
                                        </tr>
                                    ))}
                            </tbody>
                            <tfoot>
                                <tr className="fw-bold">
                                    <td colSpan={2}>Усього</td>
                                    <td>{formatMoney(derivedTotals.advanceTotal)}</td>
                                </tr>
                            </tfoot>
                        </Table>
                    )}
                </Tab>

                <Tab eventKey="profiles" title="Профілі ЗП">
                    <div className="mb-3 d-flex align-items-center gap-3 flex-wrap">
                        <Button onClick={() => openNewProfile()}>Додати співробітника</Button>
                        <AppCheckbox
                            id="salaries-show-all-profiles"
                            label="Показати звільнених і вимкнених"
                            checked={showAllProfiles}
                            onChange={(e) => setShowAllProfiles(e.target.checked)}
                        />
                    </div>
                    <Table striped bordered hover size="sm">
                        <thead className="table-dark">
                            <tr>
                                <th>Ім&apos;я</th>
                                <th>CRM id</th>
                                <th>Набір</th>
                                <th>Група</th>
                                <th>Аванс</th>
                                <th>Ставка</th>
                                <th>Відпустка/день</th>
                                <th>%</th>
                                <th>Зміна грн</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {profileRows.map(({ profile: p, user: u }) => u ? (
                                <tr key={`user-${u._id}`} className="text-muted">
                                    <td>
                                        {[u.profile?.lastName, u.profile?.firstName].filter(Boolean).join(" ") || u.username}
                                        <Badge bg="light" text="dark" className="ms-1">без профілю</Badge>
                                    </td>
                                    <td className="small">{u.profile?.keycrmManagerId ?? "—"}</td>
                                    <td></td>
                                    <td>{roleLabel(u.role, u.profile?.showroomCity)}</td>
                                    <td colSpan={5}></td>
                                    <td>
                                        <Button size="sm" variant="outline-success" onClick={() => openNewProfile(u)}>
                                            Створити профіль
                                        </Button>
                                    </td>
                                </tr>
                            ) : (
                                <tr key={p._id} className={!p.active ? "opacity-50" : ""}>
                                    <td>
                                        {p.linkedName || p.displayName}
                                        {p.linkedName && (
                                            <span className="small text-muted d-block">договір: {p.displayName}</span>
                                        )}
                                        {!p.active && <Badge bg="secondary ms-1">вимкн.</Badge>}
                                    </td>
                                    <td className="small">
                                        {p.crmManagerIdOverride != null && !Number.isNaN(Number(p.crmManagerIdOverride))
                                            ? `${p.crmManagerIdOverride} *`
                                            : p.linkedUserKeycrmManagerId ?? "—"}
                                    </td>
                                    <td className="small">{p.filterPresetName || "—"}</td>
                                    <td>{p.groupLabel}</td>
                                    <td>
                                        {p.hasAdvance === false
                                            ? "—"
                                            : `${p.advanceAmount} (${p.advanceDayOfMonth}-го)`}
                                    </td>
                                    <td>{p.baseSalary}</td>
                                    <td>{p.commissionEnabled ? `${p.commissionPercent}%` : "—"}</td>
                                    <td>{p.ratePerShift || "—"}</td>
                                    <td>{p.vacationDayRate || "—"}</td>
                                    <td>
                                        <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openEditProfile(p)}>
                                            Змінити
                                        </Button>
                                        {p.active && (
                                            <Button size="sm" variant="outline-danger" onClick={() => deactivateProfile(p._id)}>
                                                Вимкнути
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Tab>
            </Tabs>

            <Modal show={editOpen} onHide={() => setEditOpen(false)} size="lg" scrollable>
                <Modal.Header closeButton>
                    <Modal.Title>{editForm?._id ? "Профіль ЗП" : "Новий співробітник"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {editForm && (
                        <div className="d-flex flex-column gap-2">
                            <Form.Group>
                                <Form.Label>ПІБ / як у договорі</Form.Label>
                                <Form.Control
                                    value={editForm.displayName}
                                    onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Група (роль на екрані)</Form.Label>
                                {(() => {
                                    const linkedUser = editForm.userId
                                        ? users.find((x) => String(x._id) === String(editForm.userId))
                                        : null;
                                    if (linkedUser?.role) {
                                        return (
                                            <>
                                                <Form.Control
                                                    value={roleLabel(linkedUser.role, linkedUser.profile?.showroomCity)}
                                                    disabled
                                                />
                                                <Form.Text className="text-muted">
                                                    Береться з ролі користувача — змінюється на сторінці користувачів.
                                                </Form.Text>
                                            </>
                                        );
                                    }
                                    return (
                                        <Form.Control
                                            value={editForm.groupLabel}
                                            onChange={(e) => setEditForm({ ...editForm, groupLabel: e.target.value })}
                                        />
                                    );
                                })()}
                            </Form.Group>
                            <div className="row">
                                <div className="col-md-6">
                                    <Form.Group>
                                        <Form.Label>Порядок сортування</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={editForm.sortOrder}
                                            onChange={(e) => setEditForm({ ...editForm, sortOrder: e.target.value })}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-6 d-flex align-items-end pb-2">
                                    <AppCheckbox
                                        id="salary-profile-active"
                                        label="Активний у розрахунках"
                                        checked={editForm.active !== false}
                                        onChange={(e) =>
                                            setEditForm((prev) =>
                                                prev ? { ...prev, active: e.target.checked } : prev
                                            )
                                        }
                                    />
                                </div>
                            </div>
                            <Form.Group>
                                <Form.Label>Користувач системи (опційно)</Form.Label>
                                <Form.Select
                                    value={editForm.userId || ""}
                                    onChange={(e) => setEditForm({ ...editForm, userId: e.target.value })}
                                >
                                    <option value="">— не прив&apos;язано —</option>
                                    {userOptions.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Alert variant="light" className="border small py-2 mb-2">
                                KeyCRM ID менеджера:{" "}
                                <strong>
                                    {(() => {
                                        const ov = editForm.crmManagerIdOverride;
                                        if (ov !== "" && ov != null && !Number.isNaN(Number(ov))) return Number(ov);
                                        const u = users.find((x) => String(x._id) === String(editForm.userId));
                                        return u?.profile?.keycrmManagerId ?? "—";
                                    })()}
                                </strong>
                            </Alert>
                            <h6 className="text-muted mb-2">Фільтри замовлень (KeyCRM)</h6>
                            <Form.Group className="mb-2">
                                <Form.Label>Набір фільтрів</Form.Label>
                                <Form.Select
                                    value={editForm.filterPresetId || ""}
                                    onChange={(e) => setEditForm({ ...editForm, filterPresetId: e.target.value })}
                                >
                                    <option value="">— оберіть набір —</option>
                                    {filterPresets.map((pr) => (
                                        <option key={pr._id} value={pr._id}>
                                            {pr.name}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Text className="text-muted">
                                    Статуси та теги для ТО задаються в наборах на вкладці «Набори фільтрів».
                                </Form.Text>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Перевизначити ID менеджера в CRM</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={editForm.crmManagerIdOverride}
                                    onChange={(e) => setEditForm({ ...editForm, crmManagerIdOverride: e.target.value })}
                                    placeholder="Порожньо = з поля користувача KeyCRM"
                                />
                            </Form.Group>
                            <hr />
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">Умови ЗП діють з місяця</Form.Label>
                                <div style={{ maxWidth: 200 }}>
                                    <AppMonthField
                                        value={editForm.conditionsEffectiveFrom || ymNow()}
                                        onChange={(v) =>
                                            setEditForm((prev) => (prev ? { ...prev, conditionsEffectiveFrom: v } : prev))
                                        }
                                    />
                                </div>
                                <Form.Text className="text-muted">
                                    Аванс, ставка, відсоток, оплата за зміну, відпускні та «віднімати аванс» застосуються з
                                    цього місяця і далі. Попередні місяці рахуються за умовами, що діяли тоді. Дні авансу
                                    й зарплати, коментар і налаштування CRM історії не мають.
                                </Form.Text>
                            </Form.Group>
                            <AppCheckbox
                                id="salary-profile-has-advance"
                                label="Є аванс"
                                checked={editForm.hasAdvance !== false}
                                onChange={(e) =>
                                    setEditForm((prev) =>
                                        prev ? { ...prev, hasAdvance: e.target.checked } : prev
                                    )
                                }
                            />
                            <div className="row">
                                <div className="col-md-4">
                                    <Form.Group>
                                        <Form.Label>Сума авансу</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={editForm.advanceAmount}
                                            onChange={(e) => setEditForm({ ...editForm, advanceAmount: e.target.value })}
                                            disabled={editForm.hasAdvance === false}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-4">
                                    <Form.Group>
                                        <Form.Label>День авансу</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={1}
                                            max={28}
                                            value={editForm.advanceDayOfMonth}
                                            onChange={(e) => setEditForm({ ...editForm, advanceDayOfMonth: e.target.value })}
                                            disabled={editForm.hasAdvance === false}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-4">
                                    <Form.Group>
                                        <Form.Label>День зарплати</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={1}
                                            max={28}
                                            value={editForm.salaryDayOfMonth}
                                            onChange={(e) => setEditForm({ ...editForm, salaryDayOfMonth: e.target.value })}
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                            <Form.Group>
                                <Form.Label>Ставка (фікс за місяць), грн</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={editForm.baseSalary}
                                    onChange={(e) => setEditForm({ ...editForm, baseSalary: e.target.value })}
                                />
                            </Form.Group>
                            <AppCheckbox
                                id="salary-profile-commission-enabled"
                                label="Відсоток від ТО (ТО вводите в таблиці місяця)"
                                checked={editForm.commissionEnabled === true}
                                onChange={(e) =>
                                    setEditForm((prev) =>
                                        prev ? { ...prev, commissionEnabled: e.target.checked } : prev
                                    )
                                }
                            />
                            <Form.Group>
                                <Form.Label>Відсоток</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.1"
                                    value={editForm.commissionPercent}
                                    onChange={(e) => setEditForm({ ...editForm, commissionPercent: e.target.value })}
                                    disabled={editForm.commissionEnabled !== true}
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Оплата за зміну, грн</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={editForm.ratePerShift}
                                    onChange={(e) => setEditForm({ ...editForm, ratePerShift: e.target.value })}
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Відпускні за день відпустки, грн</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={editForm.vacationDayRate}
                                    onChange={(e) => setEditForm({ ...editForm, vacationDayRate: e.target.value })}
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Коментар до схеми (показується в таблиці місяця)</Form.Label>
                                <Form.Control
                                    value={editForm.commissionNote}
                                    onChange={(e) => setEditForm({ ...editForm, commissionNote: e.target.value })}
                                />
                            </Form.Group>
                            <AppCheckbox
                                id="salary-profile-deduct-advance"
                                label="Віднімати аванс від суми до виплати 1-го числа"
                                checked={editForm.deductAdvanceFromSalary !== false}
                                onChange={(e) =>
                                    setEditForm((prev) =>
                                        prev ? { ...prev, deductAdvanceFromSalary: e.target.checked } : prev
                                    )
                                }
                            />
                            {editForm._id && (editForm.conditionsHistory || []).length > 0 && (
                                <div className="mt-3">
                                    <div className="fw-semibold mb-1">Історія умов ЗП</div>
                                    <Table bordered size="sm" className="small mb-0">
                                        <thead>
                                            <tr>
                                                <th>Діє з</th>
                                                <th>Аванс</th>
                                                <th>Ставка</th>
                                                <th>%</th>
                                                <th>За зміну</th>
                                                <th>Відпускні/день</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                const list = [...editForm.conditionsHistory].sort((a, b) =>
                                                    String(a.effectiveFrom).localeCompare(String(b.effectiveFrom))
                                                );
                                                const now = ymNow();
                                                const activeFrom = list.filter((h) => h.effectiveFrom <= now).pop()?.effectiveFrom
                                                    ?? list[0]?.effectiveFrom;
                                                return list.map((h) => (
                                                    <tr
                                                        key={h.effectiveFrom}
                                                        className={h.effectiveFrom === activeFrom ? "table-success" : undefined}
                                                    >
                                                        <td>
                                                            {h.effectiveFrom}
                                                            {h.effectiveFrom === activeFrom && (
                                                                <Badge bg="success" className="ms-1">зараз</Badge>
                                                            )}
                                                            {h.effectiveFrom > now && (
                                                                <Badge bg="info" className="ms-1">заплановано</Badge>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {h.hasAdvance === false ? "—" : h.advanceAmount}
                                                            {h.hasAdvance !== false && h.deductAdvanceFromSalary === false && (
                                                                <span className="text-muted"> (не відн.)</span>
                                                            )}
                                                        </td>
                                                        <td>{h.baseSalary}</td>
                                                        <td>{h.commissionEnabled ? `${h.commissionPercent}%` : "—"}</td>
                                                        <td>{h.ratePerShift || "—"}</td>
                                                        <td>{h.vacationDayRate || "—"}</td>
                                                        <td className="text-nowrap">
                                                            <Button
                                                                size="sm"
                                                                variant="link"
                                                                className="p-0 me-2"
                                                                onClick={() => editConditionsEntry(h)}
                                                            >
                                                                Змінити
                                                            </Button>
                                                            {list.length > 1 && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="link"
                                                                    className="p-0 text-danger"
                                                                    onClick={() => deleteConditionsEntry(h.effectiveFrom)}
                                                                >
                                                                    Видалити
                                                                </Button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ));
                                            })()}
                                        </tbody>
                                    </Table>
                                    <Form.Text className="text-muted">
                                        «Змінити» підставляє умови запису у форму — після збереження оновиться саме він.
                                    </Form.Text>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setEditOpen(false)}>
                        Скасувати
                    </Button>
                    <Button variant="primary" onClick={saveProfile}>
                        Зберегти
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={refModalOpen} onHide={() => setRefModalOpen(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {refKind === "order_tag" ? "Тег" : "Статус"} у CRM
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-2">
                        <Form.Label>Назва</Form.Label>
                        <Form.Control
                            value={refForm.title}
                            onChange={(e) => setRefForm({ ...refForm, title: e.target.value })}
                        />
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label>ID у KeyCRM</Form.Label>
                        <Form.Control
                            type="number"
                            value={refForm.crmId}
                            onChange={(e) => setRefForm({ ...refForm, crmId: e.target.value })}
                        />
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label>Колір (#HEX, як у CRM)</Form.Label>
                        <Form.Control
                            value={refForm.colorHex}
                            onChange={(e) => setRefForm({ ...refForm, colorHex: e.target.value })}
                            placeholder="#FFD700"
                        />
                        <Form.Text className="text-muted">
                            Порожньо — сірий бейдж. Після імпорту з CRM підставляється автоматично, якщо API віддає колір.
                        </Form.Text>
                    </Form.Group>
                    {refForm.title && (
                        <div className="small text-muted mb-0">
                            Перегляд: <CrmColoredBadge label={refForm.title} colorHex={refForm.colorHex} />
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="d-flex flex-wrap gap-2">
                    {refForm._id && (
                        <Button
                            variant="outline-danger"
                            className="me-auto"
                            onClick={async () => {
                                const ok = await deleteRefRecord(refForm._id, {
                                    kind: refKind,
                                    title: refForm.title,
                                });
                                if (ok) setRefModalOpen(false);
                            }}
                        >
                            <Trash className="me-1" aria-hidden />
                            Видалити з довідника
                        </Button>
                    )}
                    <Button variant="secondary" onClick={() => setRefModalOpen(false)}>
                        Скасувати
                    </Button>
                    <Button variant="primary" onClick={saveRefRecord}>
                        Зберегти
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={presetModalOpen} onHide={() => setPresetModalOpen(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{presetForm?._id ? "Набір фільтрів" : "Новий набір"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {presetForm && (
                        <div className="d-flex flex-column gap-2">
                            <Form.Group>
                                <Form.Label>Назва набору</Form.Label>
                                <Form.Control
                                    value={presetForm.name}
                                    onChange={(e) => setPresetForm({ ...presetForm, name: e.target.value })}
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>ID статусів (через кому або пробіл)</Form.Label>
                                <Form.Control
                                    value={presetForm.statusIdsText}
                                    onChange={(e) => setPresetForm({ ...presetForm, statusIdsText: e.target.value })}
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Теги включити (id)</Form.Label>
                                <Form.Control
                                    value={presetForm.tagIncludeIdsText}
                                    onChange={(e) => setPresetForm({ ...presetForm, tagIncludeIdsText: e.target.value })}
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Теги виключити (id)</Form.Label>
                                <Form.Control
                                    value={presetForm.tagExcludeIdsText}
                                    onChange={(e) => setPresetForm({ ...presetForm, tagExcludeIdsText: e.target.value })}
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Режим включення тегів</Form.Label>
                                <Form.Select
                                    value={presetForm.tagIncludeMode}
                                    onChange={(e) => setPresetForm({ ...presetForm, tagIncludeMode: e.target.value })}
                                >
                                    <option value="any">Хоча б один</option>
                                    <option value="all">Усі зі списку</option>
                                </Form.Select>
                            </Form.Group>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setPresetModalOpen(false)}>
                        Скасувати
                    </Button>
                    <Button variant="primary" onClick={savePreset}>
                        Зберегти
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={!!tgSendModal} onHide={closeSalaryTelegramModal} size="lg" centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title className="h6">
                        Повідомлення в Telegram
                        {tgSendModal?.title ? ` — ${tgSendModal.title}` : ""}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {tgSendModal?.loading ? (
                        <div className="d-flex align-items-center gap-2 text-muted py-4 justify-content-center">
                            <Spinner animation="border" size="sm" />
                            Готуємо текст…
                        </div>
                    ) : tgSendModal?.loadError ? (
                        <Alert variant="danger" className="mb-0">
                            {tgSendModal.loadError}
                        </Alert>
                    ) : (
                        <>
                            {tgSendModal?.chatId != null && (
                                <p className="small text-muted mb-2">
                                    Отримувач (chat_id): <code>{tgSendModal.chatId}</code>
                                </p>
                            )}
                            <p className="small mb-2">Текст, який піде в чат (як у Telegram):</p>
                            <pre
                                className="small bg-light border rounded p-3 mb-0"
                                style={{ whiteSpace: "pre-wrap", maxHeight: "55vh", fontSize: "0.85rem" }}
                            >
                                {tgSendModal?.text || "—"}
                            </pre>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeSalaryTelegramModal} disabled={tgSendModal?.sending}>
                        Скасувати
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => void confirmSendSalaryTelegram()}
                        disabled={
                            !tgSendModal ||
                            tgSendModal.loading ||
                            Boolean(tgSendModal.loadError) ||
                            tgSendModal.sending
                        }
                    >
                        {tgSendModal?.sending ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Надсилання…
                            </>
                        ) : (
                            "Надіслати в Telegram"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Offcanvas show={salariesToolsOpen} onHide={() => setSalariesToolsOpen(false)} placement="end">
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>Налаштування ЗП</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <p className="small text-muted mb-3">
                        Довідники, набори фільтрів, режим лише авансу та профілі — окремі вкладки цієї сторінки.
                    </p>
                    <ListGroup variant="flush">
                        <ListGroup.Item
                            action
                            onClick={() => {
                                setTab("desk");
                                setSalariesToolsOpen(false);
                            }}
                        >
                            Розрахунок
                        </ListGroup.Item>
                        <ListGroup.Item
                            action
                            onClick={() => {
                                setTab("crm-dict");
                                setSalariesToolsOpen(false);
                            }}
                        >
                            Довідник CRM
                        </ListGroup.Item>
                        <ListGroup.Item
                            action
                            onClick={() => {
                                setTab("presets");
                                setSalariesToolsOpen(false);
                            }}
                        >
                            Набори фільтрів
                        </ListGroup.Item>
                        <ListGroup.Item
                            action
                            onClick={() => {
                                setTab("advance");
                                setSalariesToolsOpen(false);
                            }}
                        >
                            Лише аванс
                        </ListGroup.Item>
                        <ListGroup.Item
                            action
                            onClick={() => {
                                setTab("profiles");
                                setSalariesToolsOpen(false);
                            }}
                        >
                            Профілі ЗП
                        </ListGroup.Item>
                    </ListGroup>
                </Offcanvas.Body>
            </Offcanvas>
        </div>
    );
};

export default SalariesPage;
